"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, CalendarClock, TrendingUp, Check, ArrowUpRight, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface StepItem {
  number: string;
  title: string;
  iconName: "stack" | "cadence" | "results";
  description: string;
  bullet1Label: string;
  bullet1Text: string;
  bullet2Label: string;
  bullet2Text: string;
  bullet3Label: string;
  bullet3Text: string;
}
const stepsData: StepItem[] = [{
  number: "01",
  title: "Choose your SEO software stack",
  iconName: "stack",
  description: "You do not need an expensive all-in-one enterprise suite to see measurable progress. I recommend selecting two or three focused, specialised tools to keep your overheads low and your focus sharp.",
  bullet1Label: "Keyword discovery",
  bullet1Text: "Find search terms with realistic competition and clear search intent.",
  bullet2Label: "Technical auditing",
  bullet2Text: "Detect broken links, slow pages, indexation blocks, and status errors.",
  bullet3Label: "Content optimisation",
  bullet3Text: "Structure headings, page copy, and meta tags to match target queries."
}, {
  number: "02",
  title: "Set up your SEO workflow",
  iconName: "cadence",
  description: "Search engine optimisation rewards steady consistency over occasional overhaul. Establish a repeatable weekly, fortnightly, and monthly maintenance schedule you can reliably maintain.",
  bullet1Label: "Weekly check-in",
  bullet1Text: "Review new crawl errors, check search position shifts, and verify page speed.",
  bullet2Label: "Fortnightly sprint",
  bullet2Text: "Perform on-page content checks and identify keyword gaps on priority pages.",
  bullet3Label: "Monthly review",
  bullet3Text: "Run a full site health crawl, review competitors, and analyse enquiry trends."
}, {
  number: "03",
  title: "Implement and monitor results",
  iconName: "results",
  description: "Fix your foundational technical issues first to gain quick crawlability wins. Next, publish genuinely useful content tailored to specific questions, measuring real business impact rather than vanity rankings.",
  bullet1Label: "Fix technical blockers",
  bullet1Text: "Resolve indexing bottlenecks and broken links for immediate indexing gains.",
  bullet2Label: "Target long-tail queries",
  bullet2Text: "Create direct, practical answers for specific high-intent search queries.",
  bullet3Label: "Track commercial metrics",
  bullet3Text: "Monitor organic visits, engagement duration, and qualified contact form leads."
}];
export default function DiyProcess() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        delayChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (iconName: StepItem["iconName"]) => {
    switch (iconName) {
      case "stack":
        return <Wrench className="size-5 text-primary" aria-hidden="true" />;
      case "cadence":
        return <CalendarClock className="size-5 text-primary" aria-hidden="true" />;
      case "results":
        return <TrendingUp className="size-5 text-primary" aria-hidden="true" />;
      default:
        return <Sparkles className="size-5 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="how-diy-seo-works" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-1.5 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs uppercase tracking-wider rounded-full bg-secondary text-secondary-foreground border border-border">
              <Sparkles className="size-3 text-primary mr-1" />
              Framework &amp; Methodology
            </Badge>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground">
            How DIY SEO software works
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            I help business owners avoid paying for bloated, overcomplicated platforms. With a disciplined routine and two or three focused tools, you can execute an effective search strategy entirely on your own terms.
          </p>
        </motion.div>

        {/* 3-Step Process Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-8 items-stretch">
          {stepsData.map((step, index) => <motion.div key={step.number} data-index={index} variants={itemVariants} className="h-full flex">
              <Card className="flex-1 flex flex-col justify-between bg-card text-card-foreground border border-border rounded-2xl p-6 sm:p-8 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                <CardHeader className="p-0 gap-0">
                  {/* Top Bar: Step Number & Icon */}
                  <div className="flex items-center justify-between mb-6">
                    <span className="inline-flex items-center justify-center size-10 rounded-xl bg-primary text-primary-foreground font-serif font-bold text-sm tracking-wide shadow-xs">
                      {step.number}
                    </span>
                    <div className="size-10 rounded-xl bg-muted flex items-center justify-center border border-border/80">
                      {renderIcon(step.iconName)}
                    </div>
                  </div>

                  <CardTitle className="text-xl sm:text-2xl font-serif font-semibold text-foreground tracking-tight mb-3">
                    {step.title}
                  </CardTitle>

                  <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                    {step.description}
                  </p>
                </CardHeader>

                <CardContent className="p-0 pt-6 mt-6 border-t border-border/60">
                  <p className="text-xs uppercase tracking-wider font-semibold text-foreground/80 mb-3">
                    Key practical actions
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                      <Check className="size-4 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-foreground mr-1">
                          {step.bullet1Label}:
                        </span>
                        <span className="text-muted-foreground font-sans">
                          {step.bullet1Text}
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                      <Check className="size-4 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-foreground mr-1">
                          {step.bullet2Label}:
                        </span>
                        <span className="text-muted-foreground font-sans">
                          {step.bullet2Text}
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                      <Check className="size-4 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-foreground mr-1">
                          {step.bullet3Label}:
                        </span>
                        <span className="text-muted-foreground font-sans">
                          {step.bullet3Text}
                        </span>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Advisory / Consultant Guidance Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.4
      }} transition={{
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : 0.3
      }} className="mt-12 md:mt-16 bg-muted/60 border border-border rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="text-lg sm:text-xl font-serif font-semibold text-foreground">
              Need tailored guidance configuring your setup?
            </h3>
            <p className="mt-1.5 text-sm sm:text-base text-muted-foreground font-sans">
              I can audit your current toolkit, eliminate redundant software subscriptions, and establish a custom schedule tailored to your business goals.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-xl px-6 font-medium shadow-sm shrink-0">
            <Link to="/ContactPage">
              Book a Free SEO Consultation
              <ArrowUpRight className="size-4 ml-1.5" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}